import {
  Alert,
  DatePicker,
  Dialog,
  FormSection,
  FormStack,
  InputAdornment,
  Stack,
  TextField,
} from '@all-manager/design-system-v2';
import { Controller } from 'react-hook-form';
import DescriptionFieldOk from '../../../../components/modals/components/DescriptionFieldOk';
import { Quote, QuoteCompaisonColumnDatabase } from '../../../../types';
import { useQuoteFormDialog } from './useQuoteFormDialog';

type Props = {
  groupId: string;
  columns: QuoteCompaisonColumnDatabase[];
  quote?: Quote;
  onClose: () => void;
};

const NUMBER_SLOT_PROPS = {
  htmlInput: { inputMode: 'decimal' },
  input: { endAdornment: <InputAdornment position="end">€</InputAdornment> },
} as const;

const URL_SLOT_PROPS = {
  htmlInput: { inputMode: 'url' },
} as const;

export default function QuoteFormDialog(props: Props) {
  const hook = useQuoteFormDialog(props);

  return (
    <Dialog
      open
      onClose={hook.handleClose}
      onSubmit={hook.onSubmit}
      maxWidth="md"
      fullWidth
      title={hook.isEdition ? 'Modifier le devis' : 'Nouveau devis'}
      closeLabel="Fermer"
      actions={{
        primaryAction: {
          label: hook.isEdition ? 'Enregistrer le devis' : 'Ajouter le devis',
          type: 'submit',
          loading: hook.isPending,
        },
        secondaryAction: {
          label: 'Annuler',
          onClick: hook.handleClose,
          disabled: hook.isPending,
        },
      }}
    >
      <Stack spacing={3}>
        {hook.serverError ? <Alert severity="error">{hook.serverError}</Alert> : null}

        <FormSection title="Devis">
          <Controller
            control={hook.form.control}
            name="provider"
            render={({ field, fieldState }) => (
              <TextField
                name={field.name}
                inputRef={field.ref}
                onBlur={field.onBlur}
                onChange={field.onChange}
                value={field.value}
                label="Prestataire"
                fullWidth
                required
                autoFocus
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />

          <FormStack direction="row">
            <Controller
              control={hook.form.control}
              name="issuedAt"
              render={({ field, fieldState }) => (
                <DatePicker
                  label="Date d'émission"
                  value={field.value}
                  onChange={field.onChange}
                  inputRef={field.ref}
                  fullWidth
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
            <Controller
              control={hook.form.control}
              name="expirationDate"
              render={({ field, fieldState }) => (
                <DatePicker
                  label="Date d'expiration"
                  value={field.value}
                  onChange={field.onChange}
                  inputRef={field.ref}
                  fullWidth
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </FormStack>

          <Controller
            control={hook.form.control}
            name="fileUrl"
            render={({ field, fieldState }) => (
              <TextField
                name={field.name}
                inputRef={field.ref}
                onBlur={field.onBlur}
                onChange={field.onChange}
                value={field.value}
                label="Lien vers le devis"
                placeholder="https://"
                type="url"
                fullWidth
                slotProps={URL_SLOT_PROPS}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </FormSection>

        {hook.visibleColumns.length > 0 ? (
          <FormSection title="Critères">
            {hook.visibleColumns.map((column) => (
              column.type === 'multiline' ? (
                <DescriptionFieldOk
                  key={column.id}
                  form={hook.form}
                  name={`values.${column.id}`}
                  label={column.label}
                />
              ) : (
                <Controller
                  key={column.id}
                  control={hook.form.control}
                  name={`values.${column.id}`}
                  render={({ field, fieldState }) => (
                    <TextField
                      name={field.name}
                      inputRef={field.ref}
                      onBlur={field.onBlur}
                      onChange={field.onChange}
                      value={field.value ?? ''}
                      label={column.label}
                      fullWidth
                      slotProps={column.type === 'number' ? NUMBER_SLOT_PROPS : undefined}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              )
            ))}
          </FormSection>
        ) : null}
      </Stack>
    </Dialog>
  );
}
